import type { DiscoverDomain } from "~/react/types/discover"
import { Icon } from "~/react/ui/icon"
import { Tag, TagLabel } from "~/react/ui/tag"
import { Tooltip, TooltipContent, TooltipTrigger } from "~/react/ui/tooltip"
import {
  Table,
  TableBody,
  TableRow,
  TableCell,
} from "~/react/ui/table"
import { getCompanyLogoUrl } from "~/react/components/discover_filters/domain-helpers"
import defaultCompanyLogoUrl from "~/images/default_company_logo.svg"

const t = (key: string, options?: Record<string, unknown>) =>
  window.i18n.t(key, options)

interface TamResultsTableProps {
  domains: DiscoverDomain[]
}

function TamResultsRow({ domain }: { domain: DiscoverDomain }) {
  const locationParts = [
    domain.city,
    domain.stateName,
    domain.countryName,
  ].filter(Boolean)
  const locationTooltip = locationParts.join(", ")
  const displayLocation = domain.city || domain.stateName || domain.countryName

  const truncatedIndustry =
    domain.industryName && domain.industryName.length > 30
      ? domain.industryName.slice(0, 27) + "..."
      : domain.industryName

  return (
    <TableRow>
      <TableCell className="companies-table__company tam-results-row__company">
        <div className="h-table-company">
          <img
            className="h-table-company__logo"
            src={getCompanyLogoUrl(domain.value)}
            alt=""
            onError={(e) => {
              e.currentTarget.src = defaultCompanyLogoUrl
            }}
          />
          <div className="h-table-company__infos">
            <div className="h-table-company__heading">
              {domain.companyName ? (
                <>
                  <strong className="h-table-company__name">
                    {domain.companyName}
                  </strong>
                  <span className="h-table-company__domain">
                    {domain.value}
                  </span>
                </>
              ) : (
                <strong className="h-table-company__name">
                  {domain.value}
                </strong>
              )}
            </div>
            <ul className="h-table-company__metas">
              {domain.size && (
                <li className="h-table-company__meta">
                  <Icon icon="users" className="h-table-company__meta-icon" />
                  {domain.size}
                </li>
              )}
              {domain.industryName && (
                <li className="h-table-company__meta">
                  <Icon
                    icon="buildings"
                    className="h-table-company__meta-icon"
                  />
                  <Tooltip delayDuration={300}>
                    <TooltipTrigger asChild>
                      <span>{truncatedIndustry}</span>
                    </TooltipTrigger>
                    {domain.industryName.length > 30 && (
                      <TooltipContent side="top">
                        {domain.industryName}
                      </TooltipContent>
                    )}
                  </Tooltip>
                </li>
              )}
              {displayLocation && (
                <li className="h-table-company__meta">
                  <Icon
                    icon="map-location-dot"
                    className="h-table-company__meta-icon"
                  />
                  <Tooltip delayDuration={300}>
                    <TooltipTrigger asChild>
                      <span>{displayLocation}</span>
                    </TooltipTrigger>
                    <TooltipContent side="top">{locationTooltip}</TooltipContent>
                  </Tooltip>
                </li>
              )}
            </ul>
          </div>
        </div>
      </TableCell>
      <TableCell className="tam-results-row__emails">
        <div className="tam-results-row__emails-count">
          {domain.emailsCount > 0 ? (
            <Tag size="sm">
              <TagLabel>
                {t("js.visitors.tam_calculator.results_table.emails_available", {
                  count: domain.emailsCount,
                })}
              </TagLabel>
            </Tag>
          ) : (
            <span className="text-muted">
              {t("js.visitors.tam_calculator.results_table.no_emails")}
            </span>
          )}
        </div>
      </TableCell>
    </TableRow>
  )
}

export function TamResultsTable({ domains }: TamResultsTableProps) {
  if (!domains || domains.length === 0) {
    return null
  }

  return (
    <div className="tam-calculator__table-wrapper">
      <Table size="sm" className="tam-calculator__table">
        <TableBody>
          {domains.map((domain) => (
            <TamResultsRow key={domain.id} domain={domain} />
          ))}
        </TableBody>
      </Table>
    </div>
  )
}

